import theme from "@shared/utils/themeColors";
import { formatDistance } from "date-fns";
import { enUS } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNotificationsContext } from "../../context/NotificationsContext";
import { useActorProfiles } from "../../hooks/useActorProfiles";
import { Notification, NotificationType } from "../../types/notification.types";

interface NotificationItemProps {
  notification: Notification;
  onClose?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClose,
}) => {
  const { deleteNotification } = useNotificationsContext();

  const { getActorProfile } = useActorProfiles();
  const [actorName, setActorName] = useState<string>();
  const [isLoadingActor, setIsLoadingActor] = useState(false);

  useEffect(() => {
    const loadActorName = async () => {
      if (
        notification.data.actorId &&
        notification.message.includes("{actor}")
      ) {
        setIsLoadingActor(true);
        try {
          const actorProfile = await getActorProfile(notification.data.actorId);
          setActorName(actorProfile?.name || "User");
        } catch (error) {
          console.error("Error loading actor name:", error);
          setActorName("User");
        } finally {
          setIsLoadingActor(false);
        }
      }
    };

    loadActorName();
  }, [notification.data.actorId, notification.message, getActorProfile]);

  const getNotificationContent = () => {
    switch (notification.type) {
      case NotificationType.CONNECTION_REQUEST:
        return {
          icon: "👥",
          link: `/community/profile/${notification.data.senderId}`,
        };
      case NotificationType.CONNECTION_ACCEPTED:
        return {
          icon: "✅",
          link: `/community/profile/${notification.data.receiverId}`,
        };
      case NotificationType.CONNECTION_REJECTED:
        return {
          icon: "❌",
          link: `/community/profile/${notification.data.receiverId}`,
        };
      case NotificationType.NEW_MESSAGE:
        return {
          icon: "💬",
          link: `/messages`,
        };
      case NotificationType.POST_LIKE:
        return {
          icon: "❤️",
          link: `/community/post/${notification.data.postId}`,
        };
      case NotificationType.POST_COMMENT:
        return {
          icon: "💭",
          link: `/community/post/${notification.data.postId}`,
        };
      case NotificationType.POST_SHARE:
        return {
          icon: "🔄",
          link: `/community/post/${notification.data.postId}`,
        };
      case NotificationType.SYSTEM:
        return {
          icon: "🔔",
          link: "#",
        };
      default:
        return {
          icon: "📢",
          link: "#",
        };
    }
  };

  const content = getNotificationContent();
  const timeAgo = formatDistance(notification.createdAt.toDate(), new Date(), {
    addSuffix: true,
    locale: enUS,
  });

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteNotification(notification.id);
      onClose?.();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const formatMessage = (message: string) => {
    if (!message.includes("{actor}")) return message;
    return isLoadingActor
      ? message.replace("{actor}", "...")
      : message.replace("{actor}", actorName || "User");
  };

  return (
    <li
      className={`border-b border-gray-200 dark:border-gray-700/60 last:border-0 ${
        !notification.read ? "bg-blue-50 dark:bg-blue-900/10" : ""
      }`}
    >
      <Link
        className="block py-2 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/20 relative group"
        to={content.link}
        onClick={onClose}
      >
        <span className="block text-sm mb-2">
          {content.icon}{" "}
          <span className={`font-medium ${theme.text.primary}`}>
            {notification.title}
          </span>{" "}
          {formatMessage(notification.message)}
        </span>
        <span
          className={`block text-xs font-medium text-gray-400 dark: ${theme.text.muted}`}
        >
          {timeAgo}
        </span>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-full"
          title="Delete notification"
        >
          <svg
            className={`fill-current text-gray-400 dark: ${theme.text.muted}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
          </svg>
        </button>
      </Link>
    </li>
  );
};
